/**
 * BL-1687 (ADR 034 — Field Editability) — `evaluateAuctionFieldEditability`:
 * the AUCTION field-editability policy, the auction-side sibling of
 * `evaluateLotFieldEditability` (BL-1068). Answers, per auction field: *is it
 * editable right now, and does changing it deserve a caution?*
 *
 * **The signed record is the matrix + warnings artifacts, not this docblock.**
 * Matrix (Michael Lawrie, 2026-09-11; eighth settings row Dan Saper, 2026-09-11):
 * `docs/artifacts/auction-field-editability-matrix-09-11-26.html`. Copy:
 * `docs/artifacts/auction-settings-warnings-09-11-26.html`. Rendered as prose in
 * `docs/feature/admin/auction-field-editability-matrix.md`. Changing any cell
 * or any sentence is a re-sign per ADR 034.
 *
 * **One question per field: at what point does changing it deserve a warning?**
 * BL-1072's model sorted fields into classes (MECHANICS, COMMUNICATION, …) and
 * asked what each class does per phase — which produced 81 hard refusals and
 * needed an escape hatch (the stagger-only bypass) to be usable at all. The
 * signed policy has three answers plus one band, the zone, and one exception:
 *
 *  - **silent** — editable in every phase, no caution.
 *  - **caution from pre-bids** — once bids or pre-bids exist, a change has a
 *    consequence worth naming ({@link PRE_BIDS_OR_LATER_PHASES}).
 *  - **caution from bidding open** — the registration rules, the card hold,
 *    the premium and the clerk's close controls warn once bidding has opened
 *    ({@link LIVE_OR_LATER_PHASES}); registration rules still move freely while
 *    bids are provisional (D4).
 *  - **the schedule** — anything that moves or re-reads an unclosed lot's clock
 *    (the dates, the stagger pair, the zone) is `liveSafe ? caution : editable`
 *    while the sale can still run, keyed on {@link isLiveSafe}(lots) — a Draft
 *    auction holding a sold lot cautions; an Active auction whose lots are all
 *    drafts is silent. D3 put the stagger pair here: retiming by stagger and
 *    retiming by close date have the same consequence, so they carry one rule.
 *    Once the sale has ENDED ({@link ENDED_PHASES}) the whole schedule is
 *    LOCKED: the zone since BL-2098, the dates and the stagger pair since
 *    BL-2135 (Michael Lawrie, 2026-10-01 / 2026-10-05: a closed sale's schedule
 *    is its record). Moving the auction back to Draft unlocks it.
 *  - **immutable** — `type`, the auction format. Fixed at creation, refused in
 *    every phase including Draft, rendered on no page (D7). Kept in the union
 *    so the rule is STATED: a key absent from {@link AuctionFieldKey} is
 *    silently unlocked for every role.
 *
 * Ungoverned by decision (D5): the seller roster and per-seller commission —
 * no verdict; the settlement guard on removal is the only restriction.
 *
 * **Nothing else is ever refused** (the format, and an ended sale's schedule). Every caution names its consequence and
 * lets the operator through (PRJ-45's "warn loudly, then let them through").
 * Buyer's premium keeps its own guarded route (`PUT …/buyers-premium`, BL-984)
 * and its own impact line — its verdict here drives only the client caution.
 *
 * Pure IsomorphicService (ADR 010 / sharing.md §3): no I/O, no auth, no
 * stamp writes. Client-safe, safe inside `useMemo`. Reuses
 * `lot-field-editability.ts`'s {@link FieldVerdict} / {@link LockReason}
 * vocabulary (single source). AUCTIONS_MANAGE is a route-level gate on editing
 * at all (BUG-1140), not a policy input — the evaluator is permission-agnostic.
 *
 * **Dual enforcement, single source.** The server PATCH gate
 * (`auction-edit-gate.ts`) runs this over the auction's derived phase +
 * `liveSafe`, and — since BL-1687 AC 8 — only over fields whose VALUE changed
 * against the stored row. The client (`auction-field-lock.ts`) runs the SAME
 * function over the detail DTO's `status` + `liveSafe` for per-field hints.
 */
import type { AuctionPhaseKind } from './phase-tokens';
import type { FieldVerdict } from './lot-field-editability';

/** SILENT — editable in every phase, never cautioned. `documents` joined in
 *  BL-1687 (D2: media is silent; the separate upload status-lock was deleted
 *  with it). `bidIncrements` has been here since BUG-1338 (the ladder is
 *  stamped at creation, so a lock left no way to correct it). */
export const AUCTION_SILENT_FIELDS = [
  'heroImageStoragePath',
  'documents',
  'contactName',
  'contactPhone',
  'contactEmail',
  'loadOutInfoTemplate',
  // BL-1775: the per-auction invoice banner, stamped from the house default
  // beside `loadOutInfoTemplate` and just as silent.
  'pickupNoticeTemplate',
  'bidIncrements',
  // BL-2315: the minimum commission per auction, stamped from the house default.
  // Silent because its only consequence is the seller settlement, and the rule
  // book carries no caution justified by a settlement (field-editability-policy.md).
  'minimumCommission',
] as const;

/** CAUTION FROM PRE-BIDS — the buyer-facing listing and the terms pointer +
 *  wording (`category` and `taxRate` left with their columns in BL-1775). Once a bidder has committed anything, a change here
 *  moves what they committed against. `termsAndConditions` keeps a verdict
 *  although no auction surface writes it (D6): dropping it would unlock it for
 *  every role. */
export const AUCTION_PRE_BIDS_CAUTION_FIELDS = [
  'title',
  'description',
  'locationId',
  'noPhysicalLocation',
  'termsVersion',
  'termsAndConditions',
] as const;

/** CAUTION FROM BIDDING OPEN — registration rules, the card hold, the premium,
 *  and the clerk's two close controls (`manualCloseEnabled` is the sole wire
 *  name for `closing_type` since BL-1687; `closingType` was retired). */
export const AUCTION_BIDDING_CAUTION_FIELDS = [
  'registrationRequirements',
  'approvalMode',
  'authHoldAmount',
  'buyersPremium',
  'manualCloseEnabled',
  'lotExtensionSeconds',
  // BL-2592 — the per-auction flash-window override. Same tier as
  // `lotExtensionSeconds`: editable any time, cautioned once bidding is open.
  'flashWindowSeconds',
  // BL-2597: off-ladder max bids. Editable mid-sale (Michael Lawrie,
  // 2026-10-15): it governs which NEW maxes are accepted, never the price of a
  // standing one, so it shares the clerk close controls' sentences.
  'allowOffLadderMaxBids',
] as const;

/**
 * THE SCHEDULE — everything that moves (or, for the zone, re-reads) an unclosed
 * lot's clock. While the sale can still run it is lots-keyed:
 * `liveSafe ? caution : editable`, the phase read only to choose WHICH sentence.
 * Once the sale has ended ({@link ENDED_PHASES}) it is LOCKED.
 *
 * History: BL-1571 moved the dates out of MECHANICS onto `isLiveSafe(lots)`;
 * BUG-1528 turned the verdict from blocked to warn (blocking froze every
 * normally-closed auction against the go-live gate's own "reschedule before it
 * can go live"); BL-1621 added `timeZone`; BL-1687 D3 added the stagger pair and
 * deleted its 409. BL-2098 locked the zone on an ended sale, and BL-2135 the
 * rest of the schedule — BUG-1528's deadlock does not return, because Closed →
 * Draft unlocks the dates and the status menu sends the operator there
 * ({@link lockedReadyBlockers}).
 *
 * `auctions.time_zone` is projected onto NO realtime snapshot — see
 * `architecture.md` → "Compliant by construction — `auctions.time_zone`"
 * (ADR 024 §6 + rule #40 each suffice); two guard tests pin it.
 */
export const AUCTION_ENDED_LOCK_FIELDS = [
  'startDate',
  'firstLotCloseAt',
  'staggeredCloseEnabled',
  'staggeredCloseSeconds',
  'timeZone',
] as const;

/**
 * BL-2255 — a RING's share of the schedule, as the roster save names it: the
 * ring's close time (`auction_rings.first_lot_close_at`, `endTime` on the wire)
 * and its starting lot #. Locked once the sale has ended, like the auction's own
 * schedule; before that they answer to the two live-sale consent doors
 * (BL-1824 retime, BUG-1524 renumber), not to this rule book.
 *
 * Its own key set rather than members of {@link AuctionFieldKey}: a ring field is
 * addressed per ring (`<ringId>.endTime`), so it has no place in the Settings
 * PATCH gate or its caution collector.
 */
export const RING_SCHEDULE_FIELDS = ['endTime', 'startingLotNumber'] as const;

/** A ring field {@link evaluateRingFieldEditability} returns a verdict for. */
export type RingScheduleField = (typeof RING_SCHEDULE_FIELDS)[number];

/** IMMUTABLE — the auction format. Set at creation, refused ever after (D7). */
export const AUCTION_IMMUTABLE_FIELDS = ['type'] as const;

/** Every governed auction field the evaluator returns a verdict for. Fields not
 *  in this union (seller roster, per-seller commission, `id`, …) have no verdict. */
export type AuctionFieldKey =
  | (typeof AUCTION_SILENT_FIELDS)[number]
  | (typeof AUCTION_PRE_BIDS_CAUTION_FIELDS)[number]
  | (typeof AUCTION_BIDDING_CAUTION_FIELDS)[number]
  | (typeof AUCTION_ENDED_LOCK_FIELDS)[number]
  | (typeof AUCTION_IMMUTABLE_FIELDS)[number];

/**
 * Phases from "pre-bids are being taken" onward — the key for the
 * caution-from-pre-bids tier (BL-1687 AC 2 / D4).
 *
 * BL-1291 left `accepting-pre-bids` out of every lock set by inheritance, not by
 * decision. This set states the threshold. {@link LIVE_OR_LATER_PHASES} still
 * deliberately omits it — the two are different thresholds, not one set widened.
 */
export const PRE_BIDS_OR_LATER_PHASES: ReadonlySet<AuctionPhaseKind> = new Set([
  'accepting-pre-bids',
  'active',
  'paused',
  'closed',
  'deleted',
]);

/** Phases from "bidding has opened" onward — the key for the
 *  caution-from-bidding tier. Deliberately does NOT include `accepting-pre-bids`. */
const LIVE_OR_LATER_PHASES: ReadonlySet<AuctionPhaseKind> = new Set([
  'active',
  'paused',
  'closed',
  'deleted',
]);

/** Phases where the sale is over — selects the "This auction has ended" copy.
 *
 *  BL-1516: `post-sale` was a third member. An auction whose lots have all
 *  finished now derives `active` until the close sweep stamps `closed_at`
 *  (≤1 minute), so for that window it gets the running-sale copy variant rather
 *  than this one. Selects copy, and is the one phase set that locks fields: the
 *  schedule of an ended sale (BL-2098, BL-2135). */
const ENDED_PHASES: ReadonlySet<AuctionPhaseKind> = new Set(['closed', 'deleted']);

/** Phases where lots are actively taking bids — selects the "Bidding is open"
 *  variant of the lots-keyed cautions. */
function isSaleRunning(phase: AuctionPhaseKind): boolean {
  return phase === 'active' || phase === 'paused';
}

// ---------------------------------------------------------------------------
// Operator-facing copy. Every sentence below is signed
// (`docs/artifacts/auction-settings-warnings-09-11-26.html`); edit = re-sign.
// They ride `<FormField>`'s helperText slot / the 200 envelope's `warning`, so
// client and server read identically.
// ---------------------------------------------------------------------------

export const AUCTION_SCHEDULE_BIDDING_WARN_HINT =
  "Bidding is open — moving these dates reschedules every lot that hasn't closed yet, underneath the bidders watching them.";
export const AUCTION_SCHEDULE_WARN_HINT =
  "This auction has lots that are live or already sold. Moving these dates reschedules every lot that hasn't closed; sold lots keep their original close.";
// BUG-1713 — per-field schedule copy. The five schedule fields share ONE tier
// (liveSafe decides whether to caution) but not one sentence: only
// `firstLotCloseAt` and the stagger pair feed `computeStaggeredCloseTimes`
// (`AUCTION_SCHEDULE_COLUMNS` in the lot-schedule finalizer). Moving Opens
// changes the advertised opening time and moves no lot clock; changing the zone
// re-reads the same instants in a new zone and moves nothing (ADR 024 — Time
// Model § 4a). The two SCHEDULE_* sentences above now belong to
// `firstLotCloseAt` alone. Each new sentence names its own field ("Changing
// Opens doesn't…") rather than asserting "no lot's close time moves" outright,
// because `collectAuctionFieldWarning` joins the sentences of every field one
// save changed — moving Opens and First lot closes together must not read as a
// contradiction. Re-sign: docs/feature/admin/auction-field-editability-matrix.md.
export const AUCTION_OPENS_BIDDING_WARN_HINT =
  "Bidding is open — this changes the opening time bidders are shown. Changing when bidding opens doesn't move any lot's close time.";
export const AUCTION_OPENS_WARN_HINT =
  "This auction has lots that are live or already sold. Changing when it opens doesn't move any lot's close time.";
export const AUCTION_TIME_ZONE_BIDDING_WARN_HINT =
  "Bidding is open — bidders will see every date in the new zone. The moments themselves don't change, so changing the zone doesn't move any lot's close time.";
export const AUCTION_TIME_ZONE_WARN_HINT =
  "This auction has lots that are live or already sold. Changing the zone changes how its dates are shown, not when anything happens — it doesn't move any lot's close time.";
export const AUCTION_STAGGER_BIDDING_WARN_HINT =
  "Bidding is open — changing the gap between closes reschedules every lot that hasn't closed yet, underneath the bidders watching them.";
export const AUCTION_STAGGER_WARN_HINT =
  "This auction has lots that are live or already sold. Changing the gap between closes reschedules every lot that hasn't closed; sold lots keep their original close.";
export const AUCTION_LISTING_WARN_HINT =
  'This immediately changes what bidders are shown on the live website, including those who have already placed bids or pre-bids.';
export const AUCTION_LISTING_ENDED_HINT =
  'This auction has ended — this changes the record, not what bidders saw when they bid.';
// BL-2739 (wording Dan Saper, 2026-10-22) — the listing caution as a page reads
// it: one line at the top instead of the sentence above under every listing
// field. The server's save `warning` keeps the full listing sentence.
export const AUCTION_LIVE_PAGE_NOTICE = 'This auction is live. Changes show to bidders right away.';
export const AUCTION_TERMS_VERSION_WARN_HINT =
  'Bidding is open — bidders who accepted the current terms will be asked to accept the new ones before their next bid.';
export const AUCTION_TERMS_VERSION_ENDED_HINT =
  'This auction has ended — buyers purchased under the terms attached at the time. Changing the version now does not change what they agreed to.';
// BUG-1805 (re-signed Michael Lawrie, 2026-09-25) — the terms caution runs from
// pre-bids onward, but "Bidding is open" is false while only pre-bids are
// taken, and "the current terms" is false when nothing is attached. The
// running sentence above keeps its wording for active / paused.
export const AUCTION_TERMS_VERSION_PRE_BIDS_HINT =
  'Pre-bids are open — bidders who accepted the current terms will be asked to accept the new ones before their next bid.';
export const AUCTION_TERMS_VERSION_UNATTACHED_HINT =
  'Bidders will be asked to accept the terms you attach before their next bid.';
export const AUCTION_REGISTRATION_WARN_HINT =
  'Bidding is open — this applies to bidders who register from now on. Already-approved bidders keep their approval.';
export const AUCTION_AUTH_HOLD_WARN_HINT =
  'Bidding is open — this applies to holds taken from now on. Holds already placed are unchanged.';
export const AUCTION_BP_WARN_HINT =
  "Changing buyer's premium now won't affect lots already invoiced.";
export const AUCTION_CLERK_LIVE_WARN_HINT =
  "Bidding is open — this applies to lots that haven't closed yet.";
export const AUCTION_CLERK_LIVE_ENDED_HINT =
  'This auction has ended — changing this now has no effect.';
// BL-2098 (Michael Lawrie, 2026-10-01) — the zone's lock once the sale has ended.
export const AUCTION_TIME_ZONE_ENDED_LOCK_HINT = 'Locked after the auction closes';
// BL-2135 — the dates' and the stagger pair's lock once the sale has ended. Names
// the way out, because moving any of them is a reschedule and Draft is where a
// reschedule happens.
export const AUCTION_SCHEDULE_ENDED_LOCK_HINT =
  'Locked after the auction closes. Move it back to Draft to reschedule.';
export const AUCTION_TYPE_IMMUTABLE_HINT =
  "The auction format is set when the auction is created and can't be changed.";

type CautionedField = Exclude<
  AuctionFieldKey,
  (typeof AUCTION_SILENT_FIELDS)[number] | (typeof AUCTION_IMMUTABLE_FIELDS)[number]
>;

/**
 * Which sentences each cautioned field carries: `run` while the sale is
 * running (or, for the phase tiers, from its threshold until it ends), `ended`
 * once the sale is over. For the lots-keyed band `run` is the "Bidding is open"
 * variant and `ended` the "lots live or sold" variant — selected by
 * {@link isSaleRunning} rather than {@link ENDED_PHASES}, because a Draft
 * auction holding a sold lot must not be told bidding is open.
 *
 * `preBids` (BUG-1805) replaces `run` in `accepting-pre-bids`, for a field whose
 * `run` sentence names bidding as open. A field without one is phase-neutral.
 */
const CAUTION_COPY: Readonly<Record<CautionedField, { run: string; ended: string; preBids?: string }>> = {
  title: { run: AUCTION_LISTING_WARN_HINT, ended: AUCTION_LISTING_ENDED_HINT },
  description: { run: AUCTION_LISTING_WARN_HINT, ended: AUCTION_LISTING_ENDED_HINT },
  locationId: { run: AUCTION_LISTING_WARN_HINT, ended: AUCTION_LISTING_ENDED_HINT },
  noPhysicalLocation: { run: AUCTION_LISTING_WARN_HINT, ended: AUCTION_LISTING_ENDED_HINT },
  termsVersion: { run: AUCTION_TERMS_VERSION_WARN_HINT, ended: AUCTION_TERMS_VERSION_ENDED_HINT, preBids: AUCTION_TERMS_VERSION_PRE_BIDS_HINT },
  termsAndConditions: { run: AUCTION_TERMS_VERSION_WARN_HINT, ended: AUCTION_TERMS_VERSION_ENDED_HINT, preBids: AUCTION_TERMS_VERSION_PRE_BIDS_HINT },
  registrationRequirements: { run: AUCTION_REGISTRATION_WARN_HINT, ended: AUCTION_CLERK_LIVE_ENDED_HINT },
  approvalMode: { run: AUCTION_REGISTRATION_WARN_HINT, ended: AUCTION_CLERK_LIVE_ENDED_HINT },
  authHoldAmount: { run: AUCTION_AUTH_HOLD_WARN_HINT, ended: AUCTION_CLERK_LIVE_ENDED_HINT },
  buyersPremium: { run: AUCTION_BP_WARN_HINT, ended: AUCTION_BP_WARN_HINT },
  manualCloseEnabled: { run: AUCTION_CLERK_LIVE_WARN_HINT, ended: AUCTION_CLERK_LIVE_ENDED_HINT },
  lotExtensionSeconds: { run: AUCTION_CLERK_LIVE_WARN_HINT, ended: AUCTION_CLERK_LIVE_ENDED_HINT },
  flashWindowSeconds: { run: AUCTION_CLERK_LIVE_WARN_HINT, ended: AUCTION_CLERK_LIVE_ENDED_HINT },
  allowOffLadderMaxBids: { run: AUCTION_CLERK_LIVE_WARN_HINT, ended: AUCTION_CLERK_LIVE_ENDED_HINT },
  startDate: { run: AUCTION_OPENS_BIDDING_WARN_HINT, ended: AUCTION_OPENS_WARN_HINT },
  firstLotCloseAt: { run: AUCTION_SCHEDULE_BIDDING_WARN_HINT, ended: AUCTION_SCHEDULE_WARN_HINT },
  timeZone: { run: AUCTION_TIME_ZONE_BIDDING_WARN_HINT, ended: AUCTION_TIME_ZONE_WARN_HINT },
  staggeredCloseEnabled: { run: AUCTION_STAGGER_BIDDING_WARN_HINT, ended: AUCTION_STAGGER_WARN_HINT },
  staggeredCloseSeconds: { run: AUCTION_STAGGER_BIDDING_WARN_HINT, ended: AUCTION_STAGGER_WARN_HINT },
};

/** The lock sentence each schedule field carries once the sale has ended. */
const ENDED_LOCK_HINT: Readonly<Record<(typeof AUCTION_ENDED_LOCK_FIELDS)[number], string>> = {
  startDate: AUCTION_SCHEDULE_ENDED_LOCK_HINT,
  firstLotCloseAt: AUCTION_SCHEDULE_ENDED_LOCK_HINT,
  staggeredCloseEnabled: AUCTION_SCHEDULE_ENDED_LOCK_HINT,
  staggeredCloseSeconds: AUCTION_SCHEDULE_ENDED_LOCK_HINT,
  timeZone: AUCTION_TIME_ZONE_ENDED_LOCK_HINT,
};

const EDITABLE: FieldVerdict = { editable: true };

/** A phase-tier caution: the field's `ended` copy once the sale is over, its
 *  `preBids` copy (when it has one) while only pre-bids are taken, else `run`. */
function phaseCaution(field: CautionedField, phase: AuctionPhaseKind): FieldVerdict {
  const copy = CAUTION_COPY[field];
  if (ENDED_PHASES.has(phase)) return { editable: true, warn: copy.ended };
  if (phase === 'accepting-pre-bids' && copy.preBids != null) return { editable: true, warn: copy.preBids };
  return { editable: true, warn: copy.run };
}

/** A lots-keyed caution: the "bidding is open" copy while the sale runs, else the
 *  "lots live or sold" copy. */
function lotsKeyedCaution(field: CautionedField, phase: AuctionPhaseKind): FieldVerdict {
  const copy = CAUTION_COPY[field];
  return { editable: true, warn: isSaleRunning(phase) ? copy.run : copy.ended };
}

const GOVERNED_SET: ReadonlySet<string> = new Set<string>([
  ...AUCTION_SILENT_FIELDS,
  ...AUCTION_PRE_BIDS_CAUTION_FIELDS,
  ...AUCTION_BIDDING_CAUTION_FIELDS,
  ...AUCTION_ENDED_LOCK_FIELDS,
  ...AUCTION_IMMUTABLE_FIELDS,
]);

/**
 * Evaluate per-field editability for an auction. Pure — same inputs → same
 * output.
 *
 * @param input.phase    The ADR-023 derived {@link AuctionPhaseKind}.
 * @param input.liveSafe Whether ANY lot of this auction is in a live-safe phase
 *   ({@link isLiveSafe}). Drives {@link AUCTION_ENDED_LOCK_FIELDS} until the
 *   sale has ended — nothing else. **Required, deliberately** — the only default an optional parameter
 *   could take is `false`, which reads "nothing is live" and would hand an
 *   operator a silent schedule on a running sale. Resolved server-side and
 *   delivered (the browser only holds a page window of lots, BL-1180).
 * @returns A verdict for every governed {@link AuctionFieldKey}.
 * @sideEffect none — pure.
 */
export function evaluateAuctionFieldEditability(input: {
  phase: AuctionPhaseKind;
  liveSafe: boolean;
}): Record<AuctionFieldKey, FieldVerdict> {
  const { phase, liveSafe } = input;
  const verdicts = {} as Record<AuctionFieldKey, FieldVerdict>;

  for (const field of AUCTION_SILENT_FIELDS) verdicts[field] = EDITABLE;

  for (const field of AUCTION_PRE_BIDS_CAUTION_FIELDS) {
    verdicts[field] = PRE_BIDS_OR_LATER_PHASES.has(phase) ? phaseCaution(field, phase) : EDITABLE;
  }

  for (const field of AUCTION_BIDDING_CAUTION_FIELDS) {
    verdicts[field] = LIVE_OR_LATER_PHASES.has(phase) ? phaseCaution(field, phase) : EDITABLE;
  }

  // BL-2098 / BL-2135: the schedule locks once the sale has ended. Before that it
  // is keyed on the LOTS, and the phase picks the sentence only.
  for (const field of AUCTION_ENDED_LOCK_FIELDS) {
    if (ENDED_PHASES.has(phase)) {
      verdicts[field] = { editable: false, reason: 'phase-locked', hint: ENDED_LOCK_HINT[field] };
    } else {
      verdicts[field] = liveSafe ? lotsKeyedCaution(field, phase) : EDITABLE;
    }
  }

  for (const field of AUCTION_IMMUTABLE_FIELDS) {
    verdicts[field] = { editable: false, reason: 'immutable', hint: AUCTION_TYPE_IMMUTABLE_HINT };
  }

  return verdicts;
}

/**
 * BL-2255 — per-field editability for a ring's schedule (see
 * {@link RING_SCHEDULE_FIELDS}). Read by the roster save's server gate and by
 * Configure rings, so the two cannot disagree.
 *
 * Keyed on {@link ENDED_PHASES} — the same set that locks the auction's own
 * schedule — and carrying the same sentence, so a closed sale is refused in the
 * same words one level down. Every other phase is editable here; whether a
 * running sale's change needs the operator's consent is the doors' question.
 *
 * @param input.phase The ADR-023 derived {@link AuctionPhaseKind}.
 * @returns A verdict for each ring schedule field.
 * @sideEffect none — pure.
 */
export function evaluateRingFieldEditability(input: {
  phase: AuctionPhaseKind;
}): Record<RingScheduleField, FieldVerdict> {
  const verdict: FieldVerdict = ENDED_PHASES.has(input.phase)
    ? { editable: false, reason: 'phase-locked', hint: AUCTION_SCHEDULE_ENDED_LOCK_HINT }
    : EDITABLE;
  return { endTime: verdict, startingLotNumber: verdict };
}

/**
 * BL-2739 — the page-level live line. Shown exactly when the evaluator gives the
 * listing fields {@link AUCTION_LISTING_WARN_HINT} (from pre-bids until the sale
 * ends), so the line and the caution it stands in for cannot disagree. A page
 * that shows the line renders the listing fields without that caution.
 *
 * @param phase The auction's ADR-023 derived {@link AuctionPhaseKind}.
 * @returns {@link AUCTION_LIVE_PAGE_NOTICE}, or `undefined` when the sale isn't live.
 * @sideEffect none — pure.
 */
export function auctionLivePageNotice(phase: AuctionPhaseKind): string | undefined {
  // `liveSafe` keys only the schedule band; the listing tier ignores it.
  const title = evaluateAuctionFieldEditability({ phase, liveSafe: false }).title;
  return title.editable && 'warn' in title && title.warn === AUCTION_LISTING_WARN_HINT ? AUCTION_LIVE_PAGE_NOTICE : undefined;
}

/**
 * BUG-1805 — the `termsVersion` caution, read against whether any terms are
 * attached. The evaluator keys on the phase alone, and its pre-bids and running
 * sentences both speak of "the current terms" bidders accepted; with nothing
 * attached there are none, so those read the unattached sentence instead. The
 * ended sentence is kept — it is about the sale's record, not a next bid.
 *
 * @param opts.warn          The evaluator's caution for `termsVersion` (via the
 *   field lock), `undefined` when the phase or the permission gate shows none.
 * @param opts.termsAttached Whether the auction has a terms version attached.
 * @returns The sentence to render, or `undefined` for no caution.
 * @sideEffect none — pure.
 */
export function termsVersionCaution(opts: { warn: string | undefined; termsAttached: boolean }): string | undefined {
  const { warn, termsAttached } = opts;
  if (warn == null || termsAttached || warn === AUCTION_TERMS_VERSION_ENDED_HINT) return warn;
  return AUCTION_TERMS_VERSION_UNATTACHED_HINT;
}

/**
 * BL-2135 — the go-live blockers an operator cannot clear from where the auction
 * stands, because the rule book locks the field each one is about.
 *
 * A Closed auction whose schedule has passed is blocked from Ready by "reschedule
 * the auction", and its dates are locked: the fix is to move it back to Draft
 * first. The status menu reads this to say so instead of offering a checklist
 * the operator cannot act on (the BUG-1528 trap). Keyed on the evaluator, so it
 * cannot disagree with what the Settings pages lock. Generic on the blocker shape
 * (`field` is the only input read), so the ready gate's type need not live here.
 *
 * @param phase    The auction's derived phase.
 * @param blockers The go-live blockers, as the transition check reports them.
 * @returns The blockers whose field is locked in `phase`, in their given order.
 * @sideEffect none — pure.
 */
export function lockedReadyBlockers<B extends { field: string }>(
  phase: AuctionPhaseKind,
  blockers: readonly B[],
): B[] {
  // `liveSafe` cannot turn a caution into a lock, so its value is immaterial here.
  const verdicts = evaluateAuctionFieldEditability({ phase, liveSafe: false });
  return blockers.filter((b) => isGovernedAuctionField(b.field) && verdicts[b.field].editable === false);
}

/** Guard: is `key` a governed auction field the evaluator produces a verdict for?
 *  Used by the server gate to skip ungoverned keys in a PATCH payload. */
export function isGovernedAuctionField(key: string): key is AuctionFieldKey {
  return GOVERNED_SET.has(key);
}
